import React, { useState } from 'react';
import Claims from './claims';
import ClaimsRegister from './register/ClaimsRegister';
import BillingConfig from './BillingConfig';
import { usePermission } from '../../hooks/usePermission';
import type { PermissionString } from '../../types/permissions';

type ClaimsView = 'generate' | 'register' | 'config';

const GENERATE = 'billing:claims:generate' as PermissionString;
const CONFIG_WRITE = 'billing:config:write' as PermissionString;

interface ClaimsWorkspaceProps {
  onGoToReconciliation?: () => void;
}

/**
 * Claims tab (Phase 11): Generate (ready-to-bill visits → CMS-1500), Register (every claim of the
 * tenant and its status) and, for owner/admin, the billing codes and rates.
 */
const ClaimsWorkspace: React.FC<ClaimsWorkspaceProps> = ({ onGoToReconciliation }) => {
  const canGenerate = usePermission(GENERATE);
  const canConfigure = usePermission(CONFIG_WRITE);
  const [view, setView] = useState<ClaimsView>(canGenerate ? 'generate' : 'register');
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);

  const views: Array<{ key: ClaimsView; label: string }> = [
    ...(canGenerate ? [{ key: 'generate' as const, label: 'Generate' }] : []),
    { key: 'register', label: 'Register' },
    ...(canConfigure ? [{ key: 'config' as const, label: 'Codes & rates' }] : []),
  ];

  return (
    <div className="rounded bg-white shadow">
      <div className="flex gap-1 border-b border-slate-100 px-4 pt-3" role="tablist" aria-label="Claims">
        {views.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            onClick={() => setView(v.key)}
            className={`rounded-t-md px-3 py-1.5 text-sm font-medium ${view === v.key ? 'border-b-2 border-slate-800 text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {view === 'generate' && (
          <Claims
            onGoToReconciliation={onGoToReconciliation}
            onOpenClaim={(claimId) => { setOpenClaimId(claimId); setView('register'); }}
          />
        )}
        {view === 'register' && <ClaimsRegister initialClaimId={openClaimId} />}
        {view === 'config' && <BillingConfig />}
      </div>
    </div>
  );
};

export default ClaimsWorkspace;
